import * as React from "react";
import { useNavigate, generatePath } from 'react-router';
import { useContext } from "react";
import { AppContext } from "../../context/AppContext";
import { DataTable } from "../DataTable";

/**
 * CompanyOverview
 * @constructor
 */
const CompanyOverview: React.FC = () => {
  const {
    services: {
      serviceUser
    },
    I18N,
    getRoutes
  } = useContext(AppContext);

  /**
   * ROUTE_CONFIG
   */
  const ROUTE_CONFIG = getRoutes();

  /**
   * navigate
   */
  const navigate = useNavigate();

  /**
   * handleView
   */
  const handleView = (id) => {
    navigate(generatePath(
      ROUTE_CONFIG.COMPANY_USER_VIEW_EDIT.path, {
        id: id
      }
    ))
  };

  /**
   * static variables
   */
  // @ts-ignore
  window.CompanyOverview = {
    serviceUser: serviceUser,
    handleView: handleView,
    I18N: I18N
  };

  /**
   * header
   */
  const header = [
    { text: "ID", value: "id" },
    { text: "Partner ID", value: "partner_id" },
    { text: "Salutation", value: "salutation" },
    { text: "Title", value: "title" },
    { text: "Firstname", value: "firstname" },
    { text: "Lastname", value: "lastname" },
    { text: "Position", value: "position" },
    { text: "Department", value: "department" },
    { text: "Fon", value: "fon" },
    { text: "Mobil", value: "mobil" },
    { text: "E-Mail", value: "email" },
    { text: "E-Mail Verified At", value: "email_verified_at" },
    { text: "LinkedIn", value: "linkedin" },
    { text: "Password At", value: "password_at" },
    { text: "Created At", value: "created_at" },
    { text: "Updated At", value: "updated_at" },
    { text: "Hash ID", value: "hashId" },
    { text: "Partner", value: "partner" }
  ];

  /**
   * columns
   */
  const columns = [
    {
      select: 0,
      cellClass: "data-id",
      headerClass: "data-id",
      hidden: true
    },
    {
      select: 1,
      cellClass: "data-partner-id",
      headerClass: "data-partner-id",
      hidden: true
    },
    {
      select: 2,
      cellClass: "data-salutation",
      headerClass: "data-salutation",
      hidden: true
    },
    {
      select: 3,
      cellClass: "data-title",
      headerClass: "data-title",
      hidden: true
    },
    {
      select: 4,
      cellClass: "data-firstname",
      headerClass: "data-firstname",
      renderHeader: function () {
        // @ts-ignore
        return window.CompanyOverview.I18N.t("Vorname")
      },
      renderHeaderReplace: true
    },
    {
      select: 5,
      cellClass: "data-lastname",
      headerClass: "data-lastname",
      renderHeader: function () {
        // @ts-ignore
        return window.CompanyOverview.I18N.t("Nachname")
      },
      renderHeaderReplace: true
    },
    {
      select: 6,
      cellClass: "data-position",
      headerClass: "data-position",
      hidden: true
    },
    {
      select: 7,
      cellClass: "data-department",
      headerClass: "data-department",
      hidden: true
    },
    {
      select: 8,
      cellClass: "data-fon",
      headerClass: "data-fon",
      hidden: true
    },
    {
      select: 9,
      cellClass: "data-mobil",
      headerClass: "data-mobil",
      hidden: true
    },
    {
      select: 10,
      cellClass: "data-email",
      headerClass: "data-email",
      // renderHeaderReplace keeps the bundle's tableRender from leaving a
      // broken {nodeName:"P", data} hybrid in this heading -- diff-dom never
      // converges on such a node and freezes the main thread (see the same
      // constraint note in ClientOverviewComponent)
      renderHeader: function () {
        // @ts-ignore
        return window.CompanyOverview.I18N.t("E-Mail")
      },
      renderHeaderReplace: true
    },
    {
      select: 11,
      cellClass: "data-email-verified-at",
      headerClass: "data-email-verified-at",
      hidden: true
    },
    {
      select: 12,
      cellClass: "data-linked-in",
      headerClass: "data-linked-in",
      hidden: true
    },
    {
      select: 13,
      cellClass: "data-password-at",
      headerClass: "data-password-at",
      hidden: true
    },
    {
      select: 14,
      cellClass: "data-created-at",
      headerClass: "data-created-at",
      hidden: true
    },
    {
      select: 15,
      cellClass: "data-updated-at",
      headerClass: "data-updated-at",
      hidden: true
    },
    {
      select: 16,
      cellClass: "data-hash-id",
      headerClass: "data-hash-id",
      hidden: true
    },
    {
      select: 17,
      cellClass: "data-partner",
      headerClass: "data-partner",
      render: function (_data, _element, _rowIndex, _cellIndex, _row, _headings) {
        let data = _row[0].data || {},
          id = data?.[0]?.data;

        return `<button
            data-small
            data-primary-dark
            data-icon-only
            title="${window.CompanyOverview.I18N.t("Benutzer anzeigen")}"
            aria-label="${window.CompanyOverview.I18N.t("Benutzer anzeigen")}"
            onClick="window.CompanyOverview.handleView('${id}')"
          >
            <i data-icon-before="eye"></i>
          </button>`
      },
      renderHeader: function () {
        // @ts-ignore
        return window.CompanyOverview.I18N.t("Aktion")
      },
      renderHeaderReplace: true
    }
  ];

  /**
   * options
   */
  const options = {
    searchable: true,
    // server-side table: the component sorts only the loaded page and sends
    // no sort parameter, so sorters would promise more than they deliver
    sortable: false,
    paging: true,
    perPage: 10
  };

  /**
   * render
   */
  return (
    <DataTable
      key="companyDataTable"
      tableId="companyDataTable"
      header={ header }
      options={ options }
      columns={ columns }
      server-side="true"
      checkboxes={ false }
      customService={ serviceUser }
      customServiceName={ "getDatatable" }
    />
  );
};

export default CompanyOverview;
